import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { DataOutResult } from '@/services/auth/types';
import { COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import {
  filterDataOutResults,
  mapDataOutToSearchResult,
  mapMParticleToSearchResult,
  mapSegmentOutToSearchResult,
} from './utils/integration-results';
import { mergeQueryStates, type SearchSource } from './utils/source-bookkeeping';

type UseDataOutSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type DataOutSource = SearchSource<DataOutResult>;

export function useDataOutSource({ workspaceId, enabled, query }: UseDataOutSourceOptions): DataOutSource {
  const [mixpanelQuery, amplitudeQuery, segmentOutQuery, mparticleQuery] = useQueries({
    queries: [
      getHydraUIAPIQueryOptions({
        queryKey: ['getDataOutMixpanel', { params: { environment_id: workspaceId } }],
        queryFn: ({ signal }) =>
          hydraUiApi.integrations.getDataOut({
            params: { path: { environment_id: String(workspaceId), identifier: 'mixpanel' } },
            signal,
          }),
        queryConfig: {
          enabled,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          throwOnError: false,
          retry: false,
          select: (data) => data.data_out,
        },
      }),
      getHydraUIAPIQueryOptions({
        queryKey: ['getDataOutAmplitude', { params: { environment_id: workspaceId } }],
        queryFn: ({ signal }) =>
          hydraUiApi.integrations.getDataOut({
            params: { path: { environment_id: String(workspaceId), identifier: 'amplitude' } },
            signal,
          }),
        queryConfig: {
          enabled,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          throwOnError: false,
          retry: false,
          select: (data) => data.data_out,
        },
      }),
      getHydraUIAPIQueryOptions({
        queryKey: ['getDataOutSegmentOut', { params: { environment_id: workspaceId } }],
        queryFn: ({ signal }) =>
          hydraUiApi.integrations.getSegmentOut({
            params: { path: { environment_id: String(workspaceId) } },
            signal,
          }),
        queryConfig: {
          enabled,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          throwOnError: false,
          retry: false,
          select: (data) => data.segment_out,
        },
      }),
      getHydraUIAPIQueryOptions({
        queryKey: ['getDataOutMParticle', { params: { environment_id: workspaceId } }],
        queryFn: ({ signal }) =>
          hydraUiApi.integrations.getMparticle({
            params: { path: { environment_id: String(workspaceId) } },
            signal,
          }),
        queryConfig: {
          enabled,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          throwOnError: false,
          retry: false,
          select: (data) => data.mparticle,
        },
      }),
    ],
  });

  const sourceState = mergeQueryStates([mixpanelQuery, amplitudeQuery, segmentOutQuery, mparticleQuery]);
  const results = useMemo(() => {
    const mappedResults = [
      mixpanelQuery.data != null ? mapDataOutToSearchResult(mixpanelQuery.data, workspaceId) : null,
      amplitudeQuery.data != null ? mapDataOutToSearchResult(amplitudeQuery.data, workspaceId) : null,
      segmentOutQuery.data != null ? mapSegmentOutToSearchResult(segmentOutQuery.data, workspaceId) : null,
      mparticleQuery.data != null ? mapMParticleToSearchResult(mparticleQuery.data, workspaceId) : null,
    ];

    return filterDataOutResults(
      mappedResults.filter((result): result is DataOutResult => result != null),
      query,
    );
  }, [mixpanelQuery.data, amplitudeQuery.data, segmentOutQuery.data, mparticleQuery.data, query, workspaceId]);

  return { ...sourceState, results };
}
